import moment from 'moment';
import { APIError } from '../APIError';
import { AgentRow, IPObj, RawAgent, Store } from '../types';
import { RiskAction } from '../actions/risk';
import { isExpired } from '../constants';

export type AgentMap = {
  [id: string]: AgentRow[];
};

export const isActive = (lastUpdated: string) => {
  if (lastUpdated && moment(lastUpdated).isValid()) {
    // reported within the last 72 hours === "online"
    return moment().subtract(3, 'days') < moment(lastUpdated);
  }
  return false;
};

/**
 * convert agent to string to enable text filtering over multiple fields
 */
export const stringifyAgent = (agent: AgentRow): string =>
  [
    agent.hostname,
    agent.userName,
    agent.ipObj.map((ip) => ip.ipAddresses).join(','),
    agent.status,
    agent.OSVersion,
    agent.heartbeatTime,
    agent.contained,
  ]
    .join('\n')
    .toLocaleLowerCase();

/**
 * convert a list of RawAgents (from API response) to a map to put in store
 */
export const flattenRawAgentList = (agentList: RawAgent[]): AgentMap =>
  agentList?.reduce((agentMap, rawAgent) => {
    // select fields used in Portal
    const {
      containment: { contained },
      hostDetail: {
        platform: { architecture, version: OSVersion },
        network: { interfaces },
        hostname,
        userName,
      },
      heartbeat: { createTime: heartbeatTime },
      queryInProgress,
      deploymentID,
      id,
      softwareVersion,
    } = rawAgent;

    // Portal will only process the first interface in the list
    const firstInterface =
      interfaces && interfaces.length > 0
        ? interfaces[0]
        : {
            name: '',
            macAddress: '',
            addresses: [
              {
                ipAddress: '',
              },
            ],
            createTime: '',
          };

    let ipObj: IPObj[] = interfaces.map((current) => ({
      name: current.name,
      macAddress: current.macAddress,
      ipAddresses: current.addresses.map((ip) => ip.ipAddress).join(', '),
    }));

    const { addresses: ipAddresses, macAddress } = firstInterface;

    let ips =
      ipAddresses && ipAddresses.length > 0
        ? ipAddresses.map((i) => i.ipAddress)
        : [''];

    if (interfaces.length > 1 && ipAddresses.length > 0) {
      ips = [ipAddresses.map((i) => i.ipAddress).join(',')];
    }

    const agentWithoutIp: Omit<AgentRow, 'ipAddress'> = {
      _flat: '',
      queryInProgress,
      deploymentID,
      id,
      ipObj,
      softwareVersion,
      macAddress,
      architecture,
      OSVersion,
      hostname,
      userName,
      heartbeatTime,
      status: isActive(heartbeatTime) ? 'Online' : 'Offline',
      contained: contained ? 'Contained' : 'Not contained',
    };

    const agentRows: AgentRow[] = ips.map((ipAddress) => {
      const agent: AgentRow = {
        ...agentWithoutIp,
        ipAddress,
      };
      agent._flat = stringifyAgent(agent);
      return agent;
    });

    // We are purposefully mutating and returning the same instance of the
    // agent map to avoid memory overhead of creating a new copy each iteration
    agentMap[id] = agentRows;
    return agentMap;
  }, {} as AgentMap);

export type State = {
  agents: {
    readonly loading: boolean;
    readonly agentMap: AgentMap;
    readonly error: APIError | null;
    readonly lastFetched: moment.Moment | null;
  };
};

export const INITIAL_RISK_STATE: State = {
  agents: {
    loading: false,
    agentMap: {},
    error: null,
    lastFetched: null,
  },
};

const riskReducer = (
  state: State = INITIAL_RISK_STATE,
  action: RiskAction,
): State => {
  switch (action.type) {
    case 'set_agents':
      return {
        ...state,
        agents: {
          ...INITIAL_RISK_STATE.agents,
          loading: false,
          error: null,
          agentMap: {
            ...(action.clearStore ? {} : state.agents.agentMap),
            ...flattenRawAgentList(action.agentList),
          },
          lastFetched: moment(),
        },
      };

    case 'set_agents_loading':
      return {
        ...state,
        agents: {
          ...state.agents,
          error: null,
          loading: action.isLoading,
        },
      };

    case 'set_agent_deactivate':
      const { [action.agentID]: toDeactivate, ...rest } = state.agents.agentMap;

      return {
        ...state,
        agents: {
          ...state.agents,
          agentMap: rest,
        },
      };

    case 'set_agents_error':
      return {
        ...state,
        agents: {
          ...INITIAL_RISK_STATE.agents,
          loading: false,
          error: action.err,
        },
      };

    default:
      return state;
  }
};

export default riskReducer;

// ------------
// Selectors
//  non-trivial selectors are cached in: ./selectors/agentSelectors.ts
// -------------
export const riskSelector = {
  getAgentMap: (store: Store): AgentMap => store.risk.agents.agentMap,
  getAgentsError: (store: Store): APIError | null => store.risk.agents.error,
  isExpiredAgents: (store: Store): boolean =>
    isExpired(store.risk.agents.lastFetched, 10),
  isLoadingAgents: (store: Store): boolean => store.risk.agents.loading,
};
